/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { ChatMessage as CdsLabsChatMessage, type ChatMessagesContainerRef } from '@coursera-web/cds-labs';
import classNames from 'classnames';

import { get as getUser } from 'js/lib/user';

import { breakpoints } from '@coursera/cds-core';
import { AIGenerateBrandedNewIcon } from '@coursera/cds-icons';

import { ProgressiveLoader, type ProgressiveLoaderProps } from 'bundles/ai-coach-platform/components/building-blocks';
import ChatFooter from 'bundles/ai-coach-platform/components/patterns/chat/ChatFooter';
import type { FeedbackIssueOption } from 'bundles/ai-coach-platform/components/patterns/chat/FeedbackModal';
import {
  ChatMessage,
  ChatMessageBody,
  type ChatMessageBodyProps,
  ChatMessageContainer,
  ChatMessageHeader,
  type ChatMessageProps,
} from 'bundles/ai-coach-platform/components/patterns/chat/common';
import ChatEmptyState from 'bundles/ai-coach-platform/components/patterns/chat/common/ChatEmptyState';
import { brandUpdateCoachMessageStyles } from 'bundles/ai-coach-platform/components/patterns/chat/common/messages/brandUpdateCoachMessageStyles';
import { MESSAGE_SENDER } from 'bundles/ai-coach-platform/components/patterns/chat/constants';
import { useCoachChatContext } from 'bundles/ai-coach-platform/components/patterns/chat/context/CoachChatContext';
import type { CoachPersona, SendMessageFunc } from 'bundles/ai-coach-platform/components/patterns/chat/types';
import {
  getCoachPersona,
  useLearnerPersona,
} from 'bundles/ai-coach-platform/components/patterns/chat/utils/personaUtils';
import { useCdsLabsChatComponents } from 'bundles/ai-coach-platform/utils/statsigFeatureFlags';

import _t from 'i18n!nls/ai-coach-platform';

import type { GlobalState, MessageTypes } from './store/types';
import { isClientMessage as isClientMessageUtil, isCoachMessage as isCoachMessageUtil } from './utils/messageUtils';

export type HideMessageType = ({
  message,
  index,
  messages,
}: {
  message: MessageTypes;
  index: number;
  messages: Array<MessageTypes>;
}) => boolean;

export type Props = {
  sendMessage: SendMessageFunc;
  emptyState?: React.ReactElement;

  renderCustomMessage?: (props: ChatMessageProps) => React.ReactElement | null;
  renderCustomMessageLoader?: (props: ProgressiveLoaderProps) => React.ReactElement | null;
  renderCustomTemporaryStreamedMessage?: (props: ChatMessageBodyProps) => React.ReactElement | null;
  renderTemporaryMessage?: () => React.ReactElement | null;

  /**
   * Hides the message in the chat. This function accepts an individual message and its index
   * returns a boolean value if the message needs to be hidden based on the function's implementation
   * at the parent level.
   * Eg: Hiding the first "initiation" message and chat summary message for coach item use case.
   */
  hideMessage?: HideMessageType;
  /** Hides all message actions (copy + feedback) on every rendered message, e.g. dialogue intro messages. */
  hideMessageActions?: boolean;
  /** Hides only the copy button (keeps feedback) on every rendered message, e.g. graded role plays. */
  hideCopyButton?: boolean;
  activeChatSessionId?: string;
  coachPersona?: CoachPersona;
  customFooter?: React.ReactElement | null;
  forceHideMessageLoader?: boolean;
  enableTextStreaming?: boolean;
  enableContinuousChat?: boolean;
  setOldestMessageRef?: (node: HTMLDivElement | null) => void;
  'aria-label'?: string;
  recommendedActionsLabel?: string;
  feedbackIssueOptions?: FeedbackIssueOption[];
  onFeedbackSubmitted?: (args: { messageId: string; userIssue?: string; userComments: string }) => void;
  /** Scoped scroll handle from ChatMessagesContainer. Native scrollIntoView bubbles to
   *  every scrollable ancestor, which shifts the page header when the chat is embedded. */
  scrollContainerRef?: React.RefObject<ChatMessagesContainerRef | null>;
};

const styles = {
  messagesContainer: css`
    padding: 0 var(--cds-spacing-200);

    /* Hide scrollbar for Chrome, Safari and Opera */
    ::-webkit-scrollbar {
      display: none;
    }

    height: 100%;
    max-height: none;

    /* Hide scrollbar for IE, Edge and Firefox */
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */

    ${breakpoints.down('xs')} {
      height: 100%;
      max-height: none;
    }
  `,
  message: css`
    margin: var(--cds-spacing-200) 0;
    word-wrap: break-word;

    svg {
      flex-shrink: 0;
    }

    scroll-margin-top: var(--cds-spacing-200);

    &:focus {
      outline: none;
    }

    .coach-chat-message-body {
      /* aligns body with start of sender name */
      margin-inline-start: 40px;
      margin-top: calc(-1 * var(--cds-spacing-100));
    }
  `,
  streamedMessage: css`
    scroll-margin-top: var(--cds-spacing-200);
  `,
  smallScreenDisclaimer: css`
    display: none;

    @container coach-chat (max-height: 480px) {
      display: block;
    }
  `,
  lastMessage: (enableTextStreaming = false, enableContinuousChat = false, isCdsLabs = false) => {
    if (enableTextStreaming) {
      // Reserve enough min-height so the last message can be scrolled to the top of the
      // container. The offset approximates header + composer + padding. The CDS Labs chat
      // uses a scoped scroll container with less surrounding chrome, so it needs a smaller
      // offset to fill the taller visible area.
      const cdsAdjustment = isCdsLabs ? 120 : 0;
      const offset = 420 - cdsAdjustment;
      const offsetMd = 360 - cdsAdjustment;
      const offsetXs = 320 - cdsAdjustment;
      return css`
        min-height: calc(100vh - ${offset}px);

        ${enableContinuousChat && 'padding-bottom: var(--cds-spacing-50);'}

        ${breakpoints.down('md')} {
          min-height: calc(100vh - ${offsetMd}px);
        }

        ${breakpoints.down('xs')} {
          min-height: calc(100vh - ${offsetXs}px);
        }
      `;
    } else {
      return null;
    }
  },
};

type LoadingStateProps = {
  isCdsLabsEnabled: boolean;
  isBrandUpdateEnabled: boolean;
  coachPersona: CoachPersona;
  action: string | undefined;
  loadingMessages: string[];
};

const renderLoadingState = ({
  isCdsLabsEnabled,
  isBrandUpdateEnabled,
  coachPersona,
  action,
  loadingMessages,
}: LoadingStateProps) => {
  if (isCdsLabsEnabled) {
    return <CdsLabsChatMessage variant="llm" isLatestMessage loadingMessages={loadingMessages} />;
  }
  if (isBrandUpdateEnabled) {
    return (
      <div css={brandUpdateCoachMessageStyles.layout}>
        <div css={brandUpdateCoachMessageStyles.sparkleIconContainer}>
          <AIGenerateBrandedNewIcon size="medium" />
        </div>
        <div css={brandUpdateCoachMessageStyles.content}>
          <ChatMessageBody>
            <ProgressiveLoader action={action} />
          </ChatMessageBody>
        </div>
      </div>
    );
  }
  return (
    <>
      <ChatMessageHeader senderName={coachPersona.name} senderAvatar={coachPersona.avatar} />
      <ChatMessageBody className="coach-chat-message-body">
        <ProgressiveLoader action={action} />
      </ChatMessageBody>
    </>
  );
};

type StreamedMessageProps = {
  isCdsLabsEnabled: boolean;
  isBrandUpdateEnabled: boolean;
  coachPersona: CoachPersona;
  temporaryStreamedMessageProps: ChatMessageBodyProps;
};

const renderStreamedMessage = ({
  isCdsLabsEnabled,
  isBrandUpdateEnabled,
  coachPersona,
  temporaryStreamedMessageProps,
}: StreamedMessageProps) => {
  if (isCdsLabsEnabled) {
    return (
      <CdsLabsChatMessage
        variant="llm"
        isLatestMessage
        content={<ChatMessageBody {...temporaryStreamedMessageProps} />}
      />
    );
  }
  if (isBrandUpdateEnabled) {
    return (
      <div css={brandUpdateCoachMessageStyles.layout}>
        <div css={brandUpdateCoachMessageStyles.sparkleIconContainer}>
          <AIGenerateBrandedNewIcon size="medium" />
        </div>
        <div css={brandUpdateCoachMessageStyles.content}>
          <ChatMessageBody {...temporaryStreamedMessageProps} />
        </div>
      </div>
    );
  }
  return (
    <>
      <ChatMessageHeader senderName={coachPersona.name} senderAvatar={coachPersona.avatar} />
      <ChatMessageBody {...temporaryStreamedMessageProps} className="coach-chat-message-body" />
    </>
  );
};

const ChatMessages = ({
  emptyState,
  hideMessage,
  hideMessageActions,
  hideCopyButton,
  sendMessage,
  activeChatSessionId: chatSessionId,
  renderCustomMessage = () => null,
  renderCustomMessageLoader = () => null,
  renderTemporaryMessage = () => null,
  renderCustomTemporaryStreamedMessage = () => null,
  coachPersona = getCoachPersona(),
  customFooter,
  forceHideMessageLoader,
  enableTextStreaming = false,
  enableContinuousChat,
  setOldestMessageRef,
  'aria-label': ariaLabel,
  recommendedActionsLabel,
  feedbackIssueOptions,
  onFeedbackSubmitted,
  scrollContainerRef,
}: Props) => {
  const { disableProgrammaticScroll, chatPrefixContent, isBrandUpdateEnabled } = useCoachChatContext();
  const isCdsLabsEnabled = useCdsLabsChatComponents();
  const thinkingMessages = [_t('Thinking...')];
  const { messages, showMessageLoader, activeChatSessionId, isMessageStreaming, streamedMessage, embeddedContents } =
    useSelector((state: GlobalState) => ({
      messages: (chatSessionId && state.messages.messagesBySessionId[chatSessionId]) || state.messages.messages,
      showMessageLoader: state.behavior.messageLoader,
      activeChatSessionId: state.sessionState?.chatSessionId,
      isMessageStreaming: state.behavior.isMessageStreaming,
      streamedMessage: state.messages.streamedMessage,
      embeddedContents: state.messages.embeddedContents,
    }));
  const { isSearchMode, searchTerm } = useCoachChatContext();

  // ref for the last message in the chat, either final message or temporary streamed message
  const lastMessageRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);

  // keep track of previous isMessageStreaming value
  const previousIsMessageStreaming = useRef(isMessageStreaming);
  // track if this is the initial load
  const isInitialLoad = useRef(true);
  // set to true when a recommended action button is clicked, so the next coach response receives focus
  const shouldFocusLastMessage = useRef(false);

  const handleRecommendedActionClick = React.useCallback<() => void>(() => {
    shouldFocusLastMessage.current = true;
  }, []);

  const messageCount = messages.length;
  const { id: userId } = getUser();

  const learnerPersona = useLearnerPersona();

  const showCoachLoader =
    !forceHideMessageLoader && showMessageLoader && (activeChatSessionId === chatSessionId || !chatSessionId);

  // scroll latest message into view every time `messageCount` updates i.e. new message is added
  // or after coach loader gets hidden (for streamed messages)
  useEffect(() => {
    const isStreamingComplete = enableTextStreaming
      ? previousIsMessageStreaming.current === true && isMessageStreaming === false
      : false;

    const skipScroll = isStreamingComplete;

    if (chatPrefixContent && messageCount <= 2) {
      // skip scroll when showing the prefix content, since we want to ensure this content to be visible
      // to the user and scrolling to the latest message might move this above the viewport.
      return;
    }

    // we already scroll to the latest streaming message so we want to skip this again in the case
    // where the user has manually scrolled down we shouldn't auto-scroll them back up to the top of the message.
    // and when a user requests to fetch earlier messages, we disable this programmatic scroll behavior so they can can see the earlier messages at the top of the chat
    if (!skipScroll && !disableProgrammaticScroll) {
      if (isCdsLabsEnabled && lastMessageRef?.current) {
        scrollContainerRef?.current?.scrollElementIntoView(lastMessageRef.current, { behavior: 'smooth' });
      } else {
        lastMessageRef?.current?.scrollIntoView?.({ behavior: 'smooth' });
      }
    }

    // a11y: when a recommended action button is clicked, it disappears from the DOM so focus cannot
    // remain on it. move focus to the new coach response instead — after streaming completes for
    // streaming responses, or once the loader is gone for non-streaming responses.
    if (shouldFocusLastMessage.current && !disableProgrammaticScroll) {
      const canFocus = enableTextStreaming ? isStreamingComplete : !showCoachLoader && !isMessageStreaming;
      if (canFocus) {
        lastMessageRef?.current?.focus();
        shouldFocusLastMessage.current = false;
      }
    }

    previousIsMessageStreaming.current = isMessageStreaming;
  }, [
    messageCount,
    showCoachLoader,
    isMessageStreaming,
    enableTextStreaming,
    disableProgrammaticScroll,
    chatPrefixContent,
    scrollContainerRef,
    isCdsLabsEnabled,
  ]);

  // Helper function to determine which ref to use
  const getMessageRef = (isLast: boolean, isFirst: boolean) => {
    if (isLast && !isMessageStreaming) return lastMessageRef;
    if (isFirst && setOldestMessageRef) return setOldestMessageRef;
    return null;
  };

  // scroll to bottom of last message on initial load
  useEffect(() => {
    if (isInitialLoad.current && messageCount && enableContinuousChat) {
      isInitialLoad.current = false;
      // a11y (MERCH-3101): skip the load-time scroll-to-latest entirely when the consumer opts out,
      // so it can't drag focus/VoiceOver into the chat on load.
      if (!disableProgrammaticScroll) {
        if (isCdsLabsEnabled && lastMessageRef?.current) {
          scrollContainerRef?.current?.scrollElementIntoView(lastMessageRef.current, {
            behavior: 'instant',
            block: 'end',
          });
        } else {
          lastMessageRef?.current?.scrollIntoView?.({ behavior: 'instant', block: 'end' });
        }
      }
    }
  }, [messageCount, enableContinuousChat, disableProgrammaticScroll, scrollContainerRef, isCdsLabsEnabled]);

  const emptyStateRenderer = () => {
    return emptyState ?? <ChatEmptyState />;
  };

  const isEmptyState = !messages?.length;

  if (isEmptyState && !chatPrefixContent) {
    return (
      <>
        {emptyStateRenderer()}
        {/* Conditionally show the disclaimer below the empty state on smaller screens so that it remains
            accessible to learners in reflow and resize text accessibility cases (WCAG 1.4.4, 1.4.10) */}
        <div css={styles.smallScreenDisclaimer}>{customFooter || <ChatFooter coachPersona={coachPersona} />}</div>
      </>
    );
  }

  const temporaryStreamedMessageProps: ChatMessageBodyProps = {
    message: {
      text: streamedMessage || '',
      type: 'text',
      sender: MESSAGE_SENDER.RESPONSE,
      showAvatar: true,
      timestamp: Date.now(),
      unread: false,
      embeddedContents: embeddedContents ?? undefined,
    },
    isStreaming: true,
  };

  return (
    // a11y implementation: https://www.w3.org/WAI/WCAG21/working-examples/aria-role-log/chatlog.html
    // see COACH-1236
    <div role="log">
      {chatPrefixContent && <div>{chatPrefixContent}</div>}
      <div
        data-testid="ai-coach-chat-messages"
        ref={messagesContainerRef}
        css={styles.messagesContainer}
        aria-label={ariaLabel ?? (isBrandUpdateEnabled ? _t('Coursera AI Chat messages') : _t('Coach chat messages'))}
        role="list"
      >
        {messages?.map((message, index) => {
          const { timestamp, sender } = message;

          const isLastMessage = index === messages.length - 1;
          const isFirstMessage = index === 0;

          const isCoachMessage = isCoachMessageUtil(sender);
          const isClientMessage = isClientMessageUtil(sender);

          if (hideMessage && hideMessage({ message, index, messages })) {
            return null;
          }

          const messageProps: ChatMessageProps = {
            message,
            index,
            messages,
            sendMessage,
            learnerPersona,
            coachPersona,
            onRecommendedActionClick: handleRecommendedActionClick,
            feedbackIssueOptions,
            onFeedbackSubmitted,
          };

          return (
            <ChatMessageContainer
              key={isCoachMessage ? message.customId : `${userId}-${timestamp.valueOf()}-${index + 1}`}
              className={classNames({
                'coach-message-client': isClientMessage,
                'coach-message-response': isCoachMessage,
              })}
              ref={getMessageRef(isLastMessage, isFirstMessage)}
              css={[
                styles.message,
                isLastMessage &&
                  isCoachMessage &&
                  styles.lastMessage(enableTextStreaming, enableContinuousChat, isCdsLabsEnabled),
              ]}
              role="listitem"
              tabIndex={isLastMessage && isCoachMessage ? -1 : undefined}
            >
              {renderCustomMessage(messageProps) ?? (
                // hide recommended actions in search mode to prevent sending new messages in this mode
                <ChatMessage
                  {...messageProps}
                  hideRecommendedActions={enableContinuousChat && isSearchMode()}
                  hideMessageActions={hideMessageActions}
                  hideCopyButton={hideCopyButton}
                  searchTerm={searchTerm}
                  recommendedActionsLabel={recommendedActionsLabel}
                />
              )}
            </ChatMessageContainer>
          );
        })}

        {renderTemporaryMessage()}

        {/* show loading message when streaming is either disabled or not yet started */}
        {showCoachLoader && !isMessageStreaming && (
          <ChatMessageContainer
            role="status"
            aria-live="polite"
            css={[styles.message, styles.lastMessage(enableTextStreaming, false, isCdsLabsEnabled)]}
            className="coach-message-loader"
          >
            {renderCustomMessageLoader({ action: messages[messages.length - 1]?.action }) ??
              renderLoadingState({
                isCdsLabsEnabled,
                isBrandUpdateEnabled,
                coachPersona,
                action: messages[messages.length - 1]?.action,
                loadingMessages: thinkingMessages,
              })}
          </ChatMessageContainer>
        )}

        {/* show temporary streamed message when available */}
        {streamedMessage && (
          <ChatMessageContainer
            className="coach-message-streamed"
            ref={lastMessageRef}
            css={[
              styles.message,
              styles.streamedMessage,
              styles.lastMessage(enableTextStreaming, enableContinuousChat, isCdsLabsEnabled),
            ]}
            aria-hidden="true" // hide from screen readers since this is a temporary message
          >
            {renderCustomTemporaryStreamedMessage(temporaryStreamedMessageProps) ??
              renderStreamedMessage({
                isCdsLabsEnabled,
                isBrandUpdateEnabled,
                coachPersona,
                temporaryStreamedMessageProps,
              })}
          </ChatMessageContainer>
        )}
        {/* Conditionally show the disclaimer below the last message on smaller screens so that it remains
            accessible to learners in reflow and resize text accessibility cases (WCAG 1.4.4, 1.4.10) */}
        <div css={styles.smallScreenDisclaimer}>{customFooter || <ChatFooter coachPersona={coachPersona} />}</div>
      </div>
    </div>
  );
};

export default ChatMessages;
